import React, { useContext, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { LegalComplianceMessage } from './drawer/LegalComplianceMessage';
import { AssistantLoader } from './drawer/AssistantLoader';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';
import { useAssistant } from '../AssistantProvider';
import { useDrawer } from '../DrawerProvider';
import { getRandomElements } from './utils';
import { PIP_QUESTIONS, GENERAL_QUESTIONS } from './constants';
import { getPageType } from '../../../utils/assistant-context-utils';

export const DrawerContent = ({
  drawerState,
  historyCleared,
  setDrawerState,
}) => {
  const { channel } = useContext(ExperienceContext);
  const { messages, historyLoading, askUserQuestion, botLoading } = useAssistant();
  const { scrollToBottom, bottomDummyElementRef } = useDrawer();

  const haveMessagesMounted = useRef(false);
  const lastUserMessageRef = useRef(null);

  const isMobile = channel === 'mobile';
  const isPIP = getPageType() === 'PIP';
  const randomSuggestQuestions = useMemo(() => getRandomElements(isPIP ? PIP_QUESTIONS : GENERAL_QUESTIONS), []);
  const lastBotMessage = messages.findLast(({ type }) => type === 'bot');
  const lastUserMessage = messages.findLast(({ type }) => type === 'user');
  const lastAtcConfirmationIndex = messages.findLastIndex(({ type }) => type === 'add-to-cart');
  const isLastMessageATCConfirmation = lastAtcConfirmationIndex === messages.length - 1;
  const suggestedQuestions = messages[messages.length - 1]?.content?.suggestedQuestions || randomSuggestQuestions;

  useEffect(() => {
    // This was scrolling the main page as well on load, timeout seems to only scroll the drawer.
    setTimeout(() => {
      scrollToBottom();
    }, 1);
  }, []);

  useEffect(() => {
    if (!isLastMessageATCConfirmation && lastUserMessageRef.current && haveMessagesMounted.current) {
      lastUserMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (isLastMessageATCConfirmation && haveMessagesMounted.current) {
      scrollToBottom();
    }

    // We want to ignore the first render of messages since we should be scrolled all the way to the bottom.
    haveMessagesMounted.current = true;
  }, [messages]);

  return (
    <>
      <div data-testid="drawer-message-history">
        <LegalComplianceMessage isMobile={isMobile} />
        {historyCleared && messages.length === 1 && (
          <Typography align="center" color="subtle" variant="body-base">Conversation cleared</Typography>
        )}
        <div className="sui-flex sui-flex-col">
          {historyLoading ? (
            <div className="sui-flex-col sui-align-center sui-py-4 sui-w-full">
              <Typography variant="body-base" color="subtle" align="center">
                Loading messages
              </Typography>
              <AssistantLoader align="center" />
            </div>
          ) : messages?.map((message, i) => {
            const showFeedback = (
              message.type === 'bot'
              && !message.feedbackProvided
              && message === lastBotMessage
              && i !== 0
            );
            return (
              <ChatMessage
                key={i}
                message={message}
                showFeedback={showFeedback}
                ref={message === lastUserMessage ? lastUserMessageRef : null}
              />
            );
          })}
        </div>
        {botLoading && <AssistantLoader />}
      </div>
      {!botLoading && (
        <SuggestedQuestions
          questions={suggestedQuestions}
          onClick={async (question) => {
            askUserQuestion(question);
            if (drawerState !== 'OPEN') setDrawerState('OPEN');
          }}
          drawerState={drawerState}
        />
      )}

      <div ref={bottomDummyElementRef} />
    </>
  );
};

DrawerContent.propTypes = {
  drawerState: PropTypes.string.isRequired,
  historyCleared: PropTypes.bool.isRequired,
  setDrawerState: PropTypes.func.isRequired,
};
